import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { RegenerateSelectionForm } from "@/components/shared/RegenerateSelectionForm";
import { api } from "@/lib/api";
import type { SaveEditorVersionHandler, SaveEditorVersionResult, SuperDocInstance } from "@/lib/superdoc-types";

const MIN_SELECTION_CHARS = 10;
const SURROUNDING_CONTEXT_CHARS = 1500;
const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 16;

type ActiveEditor = NonNullable<SuperDocInstance["activeEditor"]>;
type SelectionState = ReturnType<ActiveEditor["doc"]["selection"]["current"]>;
type SelectionTarget = NonNullable<SelectionState["target"]>;
type PresentationEditor = NonNullable<ReturnType<SuperDocInstance["getPresentationEditorForDocument"]>>;
type ReplaceTarget = ReturnType<PresentationEditor["getCurrentSelectionRange"]>["target"];

interface ExtractBlock {
  nodeId: string;
  text: string;
  tableContext?: unknown;
}

interface SelectionSnapshot {
  selectedText: string;
  from: number;
  to: number;
  target: ReplaceTarget;
  selectionTarget: SelectionTarget;
  eligible: boolean;
  reason?: string;
  position: {
    left: number;
    top: number;
  };
}

interface SuperDocRegeneratePopoverProps {
  superdoc: SuperDocInstance;
  documentId: string;
  saveDocument: SaveEditorVersionHandler;
}

function buildSurroundingText(blocks: ExtractBlock[], selectedBlockIds: string[]) {
  const selectedIndices = selectedBlockIds.map((blockId) => blocks.findIndex((block) => block.nodeId === blockId)).filter((index) => index >= 0);
  if (selectedIndices.length === 0) {
    return "";
  }

  const firstIndex = Math.min(...selectedIndices);
  const lastIndex = Math.max(...selectedIndices);

  const before: string[] = [];
  let beforeChars = 0;
  for (let index = firstIndex - 1; index >= 0 && beforeChars < SURROUNDING_CONTEXT_CHARS; index -= 1) {
    const text = blocks[index]?.text.trim();
    if (!text) {
      continue;
    }
    before.unshift(text);
    beforeChars += text.length;
  }

  const after: string[] = [];
  let afterChars = 0;
  for (let index = lastIndex + 1; index < blocks.length && afterChars < SURROUNDING_CONTEXT_CHARS; index += 1) {
    const text = blocks[index]?.text.trim();
    if (!text) {
      continue;
    }
    after.push(text);
    afterChars += text.length;
  }

  return [before.join("\n\n"), after.join("\n\n")].filter(Boolean).join("\n\n");
}

function getPopoverPosition(
  presentationEditor: NonNullable<ReturnType<SuperDocInstance["getPresentationEditorForDocument"]>>,
  from: number,
  to: number,
): { left: number; top: number } {
  const selectionBounds = presentationEditor.getSelectionBounds(from, to);
  if (selectionBounds) {
    const center = selectionBounds.bounds.left + selectionBounds.bounds.width / 2;
    const left = Math.min(window.innerWidth - VIEWPORT_MARGIN - POPOVER_WIDTH / 2, Math.max(VIEWPORT_MARGIN + POPOVER_WIDTH / 2, center));
    const top = Math.min(window.innerHeight - VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, selectionBounds.bounds.bottom + 8));
    return {
      left,
      top,
    };
  }

  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const left = Math.min(window.innerWidth - VIEWPORT_MARGIN - POPOVER_WIDTH / 2, Math.max(VIEWPORT_MARGIN + POPOVER_WIDTH / 2, center));
    const top = Math.min(window.innerHeight - VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, rect.bottom + 8));
    return {
      left,
      top,
    };
  }

  return { left: VIEWPORT_MARGIN + POPOVER_WIDTH / 2, top: VIEWPORT_MARGIN };
}

function buildGenerationContext(editor: ActiveEditor, captured: SelectionSnapshot): { surroundingText: string; tableSelection: boolean } {
  const extraction = editor.doc.extract({}) as { blocks: ExtractBlock[] };
  const selectedBlockIds = captured.selectionTarget.segments.map((segment) => segment.blockId);
  const selectedBlocks = selectedBlockIds
    .map((blockId) => extraction.blocks.find((block) => block.nodeId === blockId))
    .filter((block): block is ExtractBlock => Boolean(block));

  if (selectedBlocks.some((block) => block.tableContext)) {
    return { surroundingText: "", tableSelection: true };
  }

  return {
    surroundingText: buildSurroundingText(extraction.blocks, selectedBlockIds),
    tableSelection: false,
  };
}

export function SuperDocRegeneratePopover({ superdoc, documentId, saveDocument }: SuperDocRegeneratePopoverProps) {
  const [phase, setPhase] = useState<"hidden" | "button" | "form" | "loading" | "success" | "error" | "ineligible">("hidden");
  const [snapshot, setSnapshot] = useState<SelectionSnapshot | null>(null);
  const [instruction, setInstruction] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const phaseRef = useRef(phase);
  const snapshotRef = useRef<SelectionSnapshot | null>(null);
  const requestIdRef = useRef(0);
  const isLoading = phase === "loading";

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const closePopover = useCallback(() => {
    requestIdRef.current += 1;
    setPhase("hidden");
    setSnapshot(null);
    setInstruction("");
    setStatusMessage(null);
    setRetryable(false);
  }, []);

  const readSnapshot = useCallback(
    (): SelectionSnapshot | null => {
      const editor = superdoc.activeEditor;
      const presentationEditor = superdoc.getPresentationEditorForDocument(documentId);
      if (!editor || !presentationEditor) {
        return null;
      }

      const selection = editor.doc.selection.current({ includeText: true });
      const selectedText = selection.text?.trim() ?? "";
      if (selection.empty || !selection.target || selectedText.length < MIN_SELECTION_CHARS) {
        return null;
      }

      const selectionTarget = selection.target;
      const currentRange = presentationEditor.getCurrentSelectionRange();
      const presentationTarget = currentRange.target;
      if (presentationTarget.story && presentationTarget.story.storyType !== "body") {
        return {
          selectedText,
          from: presentationEditor.state.selection.from,
          to: presentationEditor.state.selection.to,
          target: presentationTarget,
          selectionTarget,
          eligible: false,
          reason: "Can't regenerate this selection.",
          position: getPopoverPosition(presentationEditor, presentationEditor.state.selection.from, presentationEditor.state.selection.to),
        };
      }

      const from = presentationEditor.state.selection.from;
      const to = presentationEditor.state.selection.to;

      return {
        selectedText,
        from,
        to,
        target: presentationTarget,
        selectionTarget,
        eligible: true,
        position: getPopoverPosition(presentationEditor, from, to),
      };
    },
    [documentId, superdoc],
  );

  const syncSelection = useCallback(() => {
    if (phaseRef.current === "loading") {
      return;
    }

    const next = readSnapshot();
    if (!next) {
      closePopover();
      return;
    }

    setSnapshot(next);
    setStatusMessage(null);
    setRetryable(false);
    setPhase(next.eligible ? (phaseRef.current === "form" ? "form" : "button") : "ineligible");
    if (next.eligible) {
      setInstruction((current) => (phaseRef.current === "form" ? current : ""));
    } else {
      setInstruction("");
    }
  }, [closePopover, readSnapshot]);

  const refreshPosition = useCallback(() => {
    const current = snapshotRef.current;
    if (!current || phaseRef.current === "hidden") {
      return;
    }

    const presentationEditor = superdoc.getPresentationEditorForDocument(documentId);
    if (!presentationEditor) {
      return;
    }

    const nextPosition = getPopoverPosition(presentationEditor, current.from, current.to);
    setSnapshot((prev) => (prev ? { ...prev, position: nextPosition } : prev));
  }, [documentId, superdoc]);

  useEffect(() => {
    syncSelection();
  }, [syncSelection]);

  useEffect(() => {
    const handleSelectionUpdate = () => {
      syncSelection();
    };
    let boundEditor: ActiveEditor | null = null;

    const detachBoundEditor = () => {
      if (boundEditor) {
        boundEditor.off("selectionUpdate", handleSelectionUpdate);
        boundEditor = null;
      }
    };

    const bindActiveEditor = () => {
      const nextEditor = superdoc.activeEditor;
      if (nextEditor === boundEditor) {
        return;
      }
      detachBoundEditor();
      if (nextEditor) {
        nextEditor.on("selectionUpdate", handleSelectionUpdate);
        boundEditor = nextEditor;
      } else {
        closePopover();
      }
    };

    bindActiveEditor();
    const handleEditorCreate = () => {
      bindActiveEditor();
      syncSelection();
    };
    const handleEditorDestroy = () => {
      detachBoundEditor();
      closePopover();
    };

    superdoc.on("editorCreate", handleEditorCreate);
    superdoc.on("editorDestroy", handleEditorDestroy);
    document.addEventListener("scroll", refreshPosition, true);
    window.addEventListener("resize", refreshPosition);

    return () => {
      detachBoundEditor();
      superdoc.off("editorCreate", handleEditorCreate);
      superdoc.off("editorDestroy", handleEditorDestroy);
      document.removeEventListener("scroll", refreshPosition, true);
      window.removeEventListener("resize", refreshPosition);
    };
  }, [closePopover, refreshPosition, superdoc, syncSelection]);

  const handleDismiss = useCallback(() => {
    closePopover();
  }, [closePopover]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        closePopover();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [closePopover]);

  const handleGenerate = useCallback(async () => {
    const captured = readSnapshot();
    if (!captured || !captured.eligible) {
      setPhase("ineligible");
      setStatusMessage(captured?.reason ?? "Can't regenerate this selection.");
      setRetryable(false);
      return;
    }

    const editor = superdoc.activeEditor;
    if (!editor) {
      setPhase("error");
      setStatusMessage("The editor is no longer available.");
      setRetryable(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPhase("loading");
    setStatusMessage(null);
    setRetryable(false);

    try {
      const checkpoint: SaveEditorVersionResult = await saveDocument();
      if (checkpoint.status === "failed") {
        if (requestIdRef.current === requestId) {
          setPhase("error");
          setStatusMessage(checkpoint.error ?? "Could not save the current editor state before regenerating.");
          setRetryable(false);
        }
        return;
      }

      if (requestIdRef.current !== requestId) {
        return;
      }

      const generationContext = buildGenerationContext(editor, captured);
      if (generationContext.tableSelection) {
        if (requestIdRef.current === requestId) {
          setPhase("ineligible");
          setStatusMessage("Can't regenerate this selection.");
          setRetryable(false);
        }
        return;
      }

      const response = await api.documents.regenerateText(documentId, {
        selected_text: captured.selectedText,
        surrounding_text: generationContext.surroundingText,
        instruction: instruction.trim(),
      });

      const current = readSnapshot();
      if (!current || current.from !== captured.from || current.to !== captured.to || current.selectedText !== captured.selectedText) {
        if (requestIdRef.current === requestId) {
          setPhase("error");
          setStatusMessage("The selection changed before the replacement could be applied. Please try again.");
          setRetryable(false);
        }
        return;
      }

      if (requestIdRef.current !== requestId) {
        return;
      }

      editor.doc.replace({ target: captured.target, text: response.replacement_text }, { changeMode: "tracked" });
      if (requestIdRef.current !== requestId) {
        return;
      }

      const saveResult: SaveEditorVersionResult = await saveDocument({ force: true });
      if (saveResult.status !== "saved") {
        if (requestIdRef.current === requestId) {
          setPhase("error");
          setStatusMessage("The tracked change was applied in the editor, but it could not be saved. Please save manually or retry.");
          setRetryable(false);
        }
        return;
      }

      if (requestIdRef.current === requestId) {
        setPhase("success");
        setStatusMessage("Replacement applied as a tracked change.");
        setRetryable(false);
      }
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setPhase("error");
      setStatusMessage(error instanceof Error ? error.message : "Regeneration failed.");
      setRetryable(true);
    }
  }, [documentId, instruction, readSnapshot, saveDocument, superdoc]);

  if (phase === "hidden") {
    return null;
  }

  const left = snapshot?.position.left ?? 0;
  const top = snapshot?.position.top ?? 0;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 w-80 -translate-x-1/2 overflow-y-auto rounded-md border bg-card p-3 shadow-md"
      style={{ left, top, maxHeight: "calc(100vh - 1rem)" }}
    >
      {phase === "button" ? (
        <Button type="button" variant="outline" size="sm" className="w-full gap-2" onClick={() => setPhase("form")}>
          <RefreshCw className="size-4" />
          Regenerate selection
        </Button>
      ) : null}

      {phase === "form" && snapshot ? (
        <RegenerateSelectionForm
          selectedText={snapshot.selectedText}
          instruction={instruction}
          onInstructionChange={setInstruction}
          placeholder="Instruction (optional): e.g. 'make it more specific'"
          submitLabel="Regenerate"
          loadingLabel="Regenerating…"
          loading={isLoading}
          textareaClassName="h-20 resize-none text-sm"
          onCancel={handleDismiss}
          onSubmit={() => void handleGenerate()}
        />
      ) : null}

      {phase === "loading" ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Regenerating…
        </div>
      ) : null}

      {phase === "ineligible" ? (
        <div className="space-y-2">
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>{statusMessage ?? "Can't regenerate this selection."}</span>
          </p>
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={handleDismiss}>
              <X className="size-4" />
              Close
            </Button>
          </div>
        </div>
      ) : null}

      {phase === "success" ? (
        <div className="space-y-2">
          <p className="text-xs text-success">{statusMessage ?? "Replacement applied as a tracked change."}</p>
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={handleDismiss}>
              Done
            </Button>
          </div>
        </div>
      ) : null}

      {phase === "error" ? (
        <div className="space-y-2">
          <p className="flex items-start gap-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>{statusMessage ?? "Regeneration failed."}</span>
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={handleDismiss}>
              Close
            </Button>
            {retryable ? (
              <Button type="button" variant="outline" size="sm" onClick={() => void handleGenerate()}>
                <RefreshCw className="size-4" />
                Retry
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
